import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Monitor, ShieldOff, Trash2 } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB",
  { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

/**
 * Generic trusted-device manager.
 * Props: open, onOpenChange, client (axios instance), basePath ("/auth" or "/auth"),
 *        title, subjectEmail (optional, for admin/UMS view), listPath override.
 * For self-view: basePath="/auth" -> GET {basePath}/devices, POST {basePath}/devices/{id}/revoke, /devices/revoke-all.
 * For admin/UMS view over another user pass listPath / revokePath / revokeAllPath explicitly.
 */
export default function TrustedDevicesDialog({
  open, onOpenChange, client, basePath = "/auth", title = "Trusted devices",
  subjectEmail, listPath, revokePath, revokeAllPath,
}) {
  const [devices, setDevices] = useState(null);
  const [busy, setBusy] = useState(null);
  const [busyAll, setBusyAll] = useState(false);

  const _listPath = listPath || `${basePath}/devices`;
  const _revoke = revokePath || ((id) => `${basePath}/devices/${id}/revoke`);
  const _revokeAll = revokeAllPath || `${basePath}/devices/revoke-all`;

  const load = () => {
    setDevices(null);
    client.get(_listPath).then((r) => setDevices(r.data.devices)).catch(() => setDevices([]));
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line

  const revoke = async (id) => {
    setBusy(id);
    try {
      await client.post(_revoke(id));
      toast.success("Device signed out");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const revokeAll = async () => {
    setBusyAll(true);
    try {
      const { data } = await client.post(_revokeAll);
      toast.success(`Signed out of ${data.revoked} device${data.revoked === 1 ? "" : "s"}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusyAll(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none sm:max-w-lg" data-testid="trusted-devices-dialog">
        <DialogHeader>
          <DialogTitle className="text-[14px] font-bold text-[#1E293B] flex items-center gap-2">
            <Monitor className="h-4 w-4" style={{ color: "var(--brand-accent-ink, #0D9488)" }} />
            {title}{subjectEmail ? ` — ${subjectEmail}` : ""}
          </DialogTitle>
        </DialogHeader>
        <p className="text-[11px] text-slate-500 -mt-1">
          Devices where "remember me" was used. Revoking a device signs it out immediately.
        </p>

        {!devices ? <Skeleton className="h-32 w-full rounded-none" /> : devices.length === 0 ? (
          <p className="text-[12px] text-slate-400 py-6 text-center flex items-center justify-center gap-2"
            data-testid="devices-empty">
            <ShieldOff className="h-4 w-4" /> No trusted devices.
          </p>
        ) : (
          <div className="divide-y divide-slate-100 border border-slate-200 max-h-80 overflow-y-auto">
            {devices.map((d) => (
              <div key={d.id} className="px-4 py-3 flex items-center gap-3" data-testid={`device-row-${d.id}`}>
                <Monitor className="h-4 w-4 text-slate-400 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-semibold text-[#1E293B] flex items-center gap-2">
                    {d.device_label}
                    {d.current && (
                      <span className="text-[8px] font-mono uppercase px-1.5 py-0.5 border bg-teal-50 border-teal-200 text-teal-700"
                        data-testid={`device-current-${d.id}`}>this device</span>
                    )}
                  </p>
                  <p className="text-[10px] font-mono text-slate-400 mt-0.5">
                    first seen {fmt(d.created_at)} · last used {fmt(d.last_used_at)}
                  </p>
                </div>
                <button onClick={() => revoke(d.id)} disabled={busy === d.id}
                  data-testid={`device-revoke-${d.id}`}
                  className="text-[10px] font-mono uppercase border border-slate-300 px-2 py-1 hover:bg-red-50 hover:text-red-600 disabled:opacity-50 shrink-0 flex items-center gap-1">
                  {busy === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />} revoke
                </button>
              </div>
            ))}
          </div>
        )}

        {devices && devices.length > 0 && (
          <div className="flex justify-end">
            <Button onClick={revokeAll} disabled={busyAll} variant="outline"
              data-testid="devices-revoke-all-btn"
              className="rounded-none h-8 text-[11px] border-slate-300 text-slate-600 hover:text-red-600">
              {busyAll ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldOff className="h-3.5 w-3.5" />}
              Revoke all
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
